// Adaptive bitrate policy. Deliberately asymmetric per product requirements:
// step DOWN slowly (only after sustained backlog — latency is acceptable, so
// short dips are absorbed by buffering, not by quality), recover even more
// slowly, and never leave 720p — only bitrate and framerate adapt.
//
// Input each second: transport stats (bufferedAmount = bytes queued locally,
// i.e. produced but not yet flushed into ffmpeg). backlogMs is how many ms of
// video that queue represents at the current bitrate.

const FPS_STEPS = [30, 24, 20];
const DOWN_FACTOR = 0.75;
const UP_FACTOR = 1.1;
const BAD_TICKS_TO_DOWNSHIFT = 3;
const BACKLOG_BAD_MS = 1500;
const BACKLOG_SEVERE_MS = 6000;
const BACKLOG_CLEAN_MS = 300;
const CLEAN_MS_TO_UPSHIFT = 20000;

export function createAdaptiveController({ targetBitrate, floorBitrate, onChange }) {
  let bitrate = targetBitrate;
  let fpsIndex = 0;
  let badTicks = 0;
  let cleanMs = 0;

  const apply = () => onChange?.({ bitrate, fps: FPS_STEPS[fpsIndex] });

  return {
    // Called once per second with transport stats. Returns current state for
    // the UI ({ quality, bitrate, fps }).
    tick({ bufferedAmount = 0 }) {
      const backlogMs = (bufferedAmount * 8 * 1000) / bitrate;

      if (backlogMs > BACKLOG_BAD_MS) {
        badTicks += 1;
        cleanMs = 0;
      } else {
        badTicks = 0;
        if (backlogMs < BACKLOG_CLEAN_MS) cleanMs += 1000;
        else cleanMs = 0;
      }

      if (badTicks >= BAD_TICKS_TO_DOWNSHIFT) {
        badTicks = 0;
        const next = Math.max(floorBitrate, Math.round(bitrate * DOWN_FACTOR));
        const severe = backlogMs > BACKLOG_SEVERE_MS;
        if (severe && bitrate === floorBitrate && fpsIndex < FPS_STEPS.length - 1) {
          fpsIndex += 1;
          apply();
        } else if (next !== bitrate) {
          bitrate = next;
          if (severe && fpsIndex < FPS_STEPS.length - 1) fpsIndex += 1;
          apply();
        }
      }

      if (cleanMs >= CLEAN_MS_TO_UPSHIFT && (bitrate < targetBitrate || fpsIndex > 0)) {
        cleanMs = 0;
        if (bitrate < targetBitrate) {
          bitrate = Math.min(targetBitrate, Math.round(bitrate * UP_FACTOR));
        } else {
          fpsIndex -= 1; // framerate is restored last
        }
        apply();
      }

      return {
        quality: backlogMs > BACKLOG_BAD_MS || bitrate < targetBitrate * 0.6 ? "poor" : "good",
        bitrate,
        fps: FPS_STEPS[fpsIndex],
      };
    },
  };
}
